import React, { useEffect } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../i18n/LanguageContext';

const chipConfig = {
  new: { label: 'New', className: 'bg-blue-500 text-white' },
  veg: { label: 'Veg', className: 'bg-green-500 text-white', icon: 'Leaf' },
  spicy: { label: 'Spicy', className: 'bg-red-500 text-white', icon: 'Flame' },
  popular: { label: 'Popular', className: 'bg-amber-500 text-white', icon: 'Star' },
};

const DishDetailsModal = ({ dish, categoryName, onClose, onViewAR, hasARModel }) => {
  const { t } = useLanguage();

  useEffect(() => {
    if (!dish) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [dish, onClose]);

  if (!dish) return null;

  const activeChips = Array.isArray(dish?.chips) ? dish.chips : dish?.chip ? [dish.chip] : [];
  const ingredients = Array.isArray(dish?.ingredients)
    ? dish.ingredients.filter(Boolean)
    : dish?.ingredients
      ? String(dish.ingredients).split(',').map((s) => s.trim()).filter(Boolean)
      : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 px-0 sm:px-4 py-0 sm:py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dish-details-title"
    >
      <div
        className="relative w-full sm:max-w-lg bg-card rounded-t-2xl sm:rounded-2xl shadow-warm-xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-background/80 hover:bg-background border border-border transition-colors"
          aria-label={t('close')}
        >
          <Icon name="X" size={18} />
        </button>

        <div className="relative h-48 sm:h-56 shrink-0 bg-muted">
          {dish?.image ? (
            <Image
              src={dish.image}
              alt={dish?.imageAlt || dish?.name || ''}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="ChefHat" size={48} className="text-muted-foreground/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6">
          <div className="flex items-start justify-between gap-3 mb-3 pr-8">
            <div className="min-w-0 flex-1">
              <h2
                id="dish-details-title"
                className="text-xl sm:text-2xl font-heading font-semibold text-foreground leading-snug"
              >
                {dish?.name}
              </h2>
              {categoryName && (
                <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  <Icon name="Tag" size={12} />
                  {categoryName}
                </span>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-heading font-bold text-primary">
                ${dish?.price?.toFixed(2)}
              </div>
              {dish?.originalPrice && (
                <div className="text-sm text-muted-foreground line-through">
                  ${dish.originalPrice.toFixed(2)}
                </div>
              )}
            </div>
          </div>

          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {activeChips.map((chipKey) => {
                const chip = chipConfig[chipKey];
                if (!chip) return null;
                return (
                  <span
                    key={chipKey}
                    className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${chip.className}`}
                  >
                    {chip.icon && <Icon name={chip.icon} size={12} />}
                    {chip.label}
                  </span>
                );
              })}
            </div>
          )}

          {dish?.description && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {dish.description}
            </p>
          )}

          {ingredients.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {t('ingredients')}
              </h3>
              <div className="flex items-start gap-2">
                <Icon name="ChefHat" size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">{ingredients.join(', ')}</p>
              </div>
            </div>
          )}

          {dish?.calories > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Icon name="Flame" size={16} />
              <span>{dish.calories} cal</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {hasARModel && onViewAR && (
              <Button
                variant="default"
                size="default"
                onClick={() => {
                  onClose?.();
                  onViewAR(dish);
                }}
                iconName="Scan"
                iconPosition="left"
                className="flex-1 touch-target"
              >
                Voir en RA
              </Button>
            )}
            <Button
              variant={hasARModel ? 'outline' : 'default'}
              size="default"
              onClick={onClose}
              className="flex-1 touch-target"
            >
              {t('close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishDetailsModal;
